import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { observer } from 'mobx-react-lite'

import { FiltersPanel } from 'widgets/FiltersPanel/FiltersPanel'

import { SelectionBar } from 'shared/components/SelectionBar/SelectionBar'

import { useApplicationsPage } from './hooks/useApplicationsPage'
import FiltersApplicationsContent from './components/FiltersApplicationsContent/FiltersApplicationsContent'
import StatusProgressIcon from 'pages/ApplicationDetailsPage/components/StatusProgressIcon/StatusProgressIcon'
import { normalizeApplicationStatusKey } from 'pages/ApplicationDetailsPage/utils/applicationStatusUi'

import StatusRoadmap from './components/StatusRoadmap/StatusRoadmap'
import FiltersBar from './components/FiltersBar/FiltersBar'
import ColumnsHeader from './components/ColumnsHeader/ColumnsHeader'
import ApplicationsList from './components/ApplicationsList/ApplicationsList'
import ApplicationRow from './components/ApplicationRow/ApplicationRow'
import AssignActivityModal from 'widgets/AssignActivityModal/AssignActivityModal'
import GenerateLorModal from 'widgets/GenerateLorModal/GenerateLorModal'
import { useApplicationsRowViews } from './hooks/useApplicationsRowViews'
import { useGenerateLorModalController } from './hooks/useGenerateLorModalController'
import { refreshAppAssignments } from './utils/refreshAppAssignments'
import { applicationDocumentsStore } from 'pages/ApplicationDetailsPage/model/applicationDocumentsStore'
import { markApplicationSeenIfNew } from './utils/markApplicationSeen'
import ApplicationEssayReviewDrawer from './components/ApplicationEssayReviewDrawer/ApplicationEssayReviewDrawer'
import { useApplicationEssayReviewDrawer } from './hooks/useApplicationEssayReviewDrawer'

import s from './styles/ApplicationsPage.module.scss'
import { applicationsFiltersStore } from './model/applicationsFiltersStore'

export const ApplicationsPage = observer(() => {
	const location = useLocation()
	const {
		rows,
		sortKey,
		sortDir,
		onSortChange,
		baseItems,
		extraItems,
		setExtraItems,
		hasSelection,
		selectedCount,
		selectedIds,
		toggleOne,
		toggleAll,
		clearSelection,
		actionsSelection,
		isOpenFilter,
		openFilter,
		closeFilter,
		counselorStatusOptions,
		bulkUpdateApplicationsStatus,
		navigateToApplicationDetails,
		applicationsCount,
		studentsCount,
		removeOnePanelFilter,
		clearAllPanelFilters,
		clearSortState,
	} = useApplicationsPage()

	const [updateStatusOpen, setUpdateStatusOpen] = useState(false)
	const [updateStatusMenuStyle, setUpdateStatusMenuStyle] = useState(null)
	const [expandedId, setExpandedId] = useState(null)
	const [assignOpen, setAssignOpen] = useState(false)
	const [assignmentModalRow, setAssignmentModalRow] = useState(null)
	const [bragsheetAssignOpen, setBragsheetAssignOpen] = useState(false)
	const [bragsheetAssignKey, setBragsheetAssignKey] = useState(0)
	const [bragsheetAssignStudents, setBragsheetAssignStudents] = useState([])
	const [applicationModalInstanceKey, setApplicationModalInstanceKey] =
		useState(0)
	const updateStatusBtnRef = useRef(null)
	const updateStatusMenuRef = useRef(null)

	useEffect(() => {
		const needsAttention = location.state?.needsAttention
		if (!Array.isArray(needsAttention) || needsAttention.length === 0) return
		applicationsFiltersStore.setFilter('needsAttention', needsAttention)
	}, [location.key, location.state])

	const { views: rowViews } = useApplicationsRowViews(rows)
	const essayReviewDrawer = useApplicationEssayReviewDrawer()
	const generateLorModal = useGenerateLorModalController()

	const allSelected = rows.length > 0 && selectedIds.length === rows.length

	const openAssignmentModal = row => {
		setAssignmentModalRow(row)
		setApplicationModalInstanceKey(k => k + 1)
		setAssignOpen(true)
	}

	const handleAssignBragsheetFromLor = () => {
		const app = generateLorModal.application
		if (app?.studentId) {
			setBragsheetAssignStudents([
				{
					id: app.studentId,
					name: app.studentName || 'Student',
					grade: app.grade || null,
				},
			])
		}
		generateLorModal.close()
		setBragsheetAssignKey(value => value + 1)
		setBragsheetAssignOpen(true)
	}

	const bulkMenuSelectedStatus = useMemo(() => {
		if (!selectedIds?.length) return null
		const idSet = new Set(
			selectedIds.map(id => String(id || '').trim()).filter(Boolean),
		)
		const subset = rows.filter(r => idSet.has(String(r?.id || '').trim()))
		if (!subset.length) return null
		const keys = subset.map(r =>
			normalizeApplicationStatusKey(r?.applicationStatus),
		)
		const first = keys[0]
		if (!first || keys.some(k => k !== first)) return null
		return first
	}, [rows, selectedIds])

	const sortChip = useMemo(() => {
		const item = (extraItems || []).find(x => x.id === 'sort')
		return item || null
	}, [extraItems])

	const filterChips = useMemo(() => {
		const panelChips = (baseItems || [])
			.filter(
				item =>
					String(item.id).startsWith('filter:') && item.filterKey !== 'stage',
			)
			.map(item => ({
				id: item.id,
				label: item.label,
				kind: 'filter',
				filterKey: item.filterKey,
			}))

		const chips = [...panelChips]
		if (sortChip) {
			chips.push({ id: 'sort', label: sortChip.label, kind: 'sort' })
		}
		return chips
	}, [baseItems, sortChip])

	const handleRemoveChip = chip => {
		if (!chip) return
		if (chip.kind === 'sort') {
			setExtraItems([])
			clearSortState()
			return
		}
		if (chip.kind === 'filter' && chip.filterKey) {
			removeOnePanelFilter(chip.filterKey)
		}
	}

	const handleClearAll = () => {
		clearAllPanelFilters()
		setExtraItems([])
		clearSortState()
	}

	const handleSortSelect = ({ opt, name }) => {
		if (!opt?.key || !opt?.dir) return
		onSortChange({ key: opt.key, dir: opt.dir })
		setExtraItems([
			{
				id: 'sort',
				label: `${name}: ${opt.label}`,
				isActive: true,
				removable: true,
				onClick: () => {},
			},
		])
	}

	const handleToggleExpand = id => {
		setExpandedId(prev => {
			if (prev === id) return null
			const row = rowViews.find(r => String(r?.id) === String(id))
			if (row?.isNewApplication) {
				void markApplicationSeenIfNew(row)
			}
			return id
		})
	}

	useEffect(() => {
		if (!updateStatusOpen) return undefined
		const onDoc = e => {
			if (updateStatusBtnRef.current?.contains(e.target)) return
			if (updateStatusMenuRef.current?.contains(e.target)) return
			setUpdateStatusOpen(false)
		}
		document.addEventListener('mousedown', onDoc)
		return () => document.removeEventListener('mousedown', onDoc)
	}, [updateStatusOpen])

	useEffect(() => {
		if (!updateStatusOpen) return undefined
		const updateMenuPosition = () => {
			const btn = updateStatusBtnRef.current
			if (!btn) return
			const rect = btn.getBoundingClientRect()
			const menuMinWidth = 280
			const viewportPadding = 8
			const nextLeft = Math.max(
				viewportPadding,
				Math.min(
					rect.right - menuMinWidth,
					window.innerWidth - menuMinWidth - viewportPadding,
				),
			)
			setUpdateStatusMenuStyle({
				position: 'fixed',
				top: rect.bottom + 8,
				left: nextLeft,
				zIndex: 2000,
			})
		}
		updateMenuPosition()
		window.addEventListener('resize', updateMenuPosition)
		window.addEventListener('scroll', updateMenuPosition, true)
		return () => {
			window.removeEventListener('resize', updateMenuPosition)
			window.removeEventListener('scroll', updateMenuPosition, true)
		}
	}, [updateStatusOpen])

	const actionsSelectionResolved = useMemo(
		() =>
			(actionsSelection || []).map(action =>
				action.id === 'update'
					? {
							...action,
							onClick: () => setUpdateStatusOpen(v => !v),
							buttonRef: updateStatusBtnRef,
							icon: (
								<StatusProgressIcon
									status="waitlisted"
									arcColor="var(--text-purple)"
									trackColor="var(--bg-pill-purple-light)"
									ringColor="var(--color-white)"
									size={14}
								/>
							),
						}
					: action,
			),
		[actionsSelection],
	)

	const updateStatusDropdown = updateStatusOpen
		? createPortal(
				<div
					ref={updateStatusMenuRef}
					className={s.updateStatusMenu}
					role="listbox"
					style={updateStatusMenuStyle || undefined}
				>
					{counselorStatusOptions.map(opt => (
						<button
							key={opt.value}
							type="button"
							className={s.updateStatusOption}
							role="option"
							aria-selected={
								bulkMenuSelectedStatus != null &&
								bulkMenuSelectedStatus ===
									normalizeApplicationStatusKey(opt.value)
							}
							onClick={async () => {
								setUpdateStatusOpen(false)
								await bulkUpdateApplicationsStatus(opt.value)
							}}
						>
							<StatusProgressIcon
								status={opt.value}
								className={s.updateStatusOption__icon}
								size={14}
							/>
							<span className={s.updateStatusOption__label}>
								{opt.menuLabel}
							</span>
						</button>
					))}
				</div>,
				document.body,
			)
		: null

	return (
		<div className={s.page}>
			<div className={s.container}>
				<div className={s.content}>
					<h4 className={s.title}>
						Managing {applicationsCount} active university applications across{' '}
						{studentsCount} students
					</h4>

					<div className={s.filters}>
						<StatusRoadmap />

						<FiltersBar
							chips={filterChips}
							onRemoveChip={handleRemoveChip}
							onClearAll={handleClearAll}
						/>
					</div>

					<SelectionBar
						isVisible={hasSelection}
						text={
							selectedCount === 1
								? '1 application selected'
								: `${selectedCount} applications selected`
						}
						onClear={clearSelection}
						onClose={clearSelection}
						actions={actionsSelectionResolved}
					/>

					<div className={s.table}>
						<div
							className={`${s.tableBody} ${isOpenFilter ? s.tableBodyFilterOpen : ''}`.trim()}
						>
							<div className={s.tableScroll}>
								<div className={s.tableScrollInner}>
									<ColumnsHeader
										allSelected={allSelected}
										onToggleAll={() => toggleAll(rows.map(r => r.id))}
										sortKey={sortKey}
										sortDir={sortDir}
										onSortSelect={handleSortSelect}
										onOpenFilter={openFilter}
										isFilterOpen={isOpenFilter}
									/>

									<ApplicationsList isEmpty={rowViews.length === 0}>
										{rowViews.map(row => (
											<ApplicationRow
												key={row.id}
												row={row}
												selected={selectedIds.includes(row.id)}
												expanded={expandedId === row.id}
												onToggleSelect={toggleOne}
												onToggleExpand={handleToggleExpand}
												onOpenStudent={navigateToApplicationDetails}
												onOpenApplication={navigateToApplicationDetails}
												onAssignAssignment={openAssignmentModal}
												onGenerateLor={generateLorModal.open}
												onOpenEssay={essay =>
													essayReviewDrawer.openDrawer(row, essay)
												}
											/>
										))}
									</ApplicationsList>
								</div>
							</div>

							<FiltersPanel isOpen={isOpenFilter} onClose={closeFilter}>
								<FiltersApplicationsContent onClose={closeFilter} />
							</FiltersPanel>
						</div>
					</div>
				</div>
			</div>
			{updateStatusDropdown}

			<ApplicationEssayReviewDrawer controller={essayReviewDrawer} />
			<GenerateLorModal
				isOpen={generateLorModal.isOpen}
				onClose={generateLorModal.close}
				application={generateLorModal.application}
				onAssignBragsheet={handleAssignBragsheetFromLor}
			/>

			<AssignActivityModal
				key={bragsheetAssignKey}
				isOpen={bragsheetAssignOpen}
				onClose={() => setBragsheetAssignOpen(false)}
				selectedStudents={bragsheetAssignStudents}
				lockRecipients
				initialAssignmentType="bragsheet_builder"
				initialTaskTitle="Complete Brag Sheet for LoR"
				initialStepId="timeline"
				onSuccess={() => setBragsheetAssignOpen(false)}
			/>

			<AssignActivityModal
				isOpen={assignOpen}
				onClose={() => {
					setAssignOpen(false)
					setAssignmentModalRow(null)
				}}
				selectedStudents={
					assignmentModalRow
						? [
								{
									id:
										assignmentModalRow.studentId ||
										assignmentModalRow.studentName ||
										assignmentModalRow.id,
									name: assignmentModalRow.studentName || 'Student',
									grade: assignmentModalRow.grade || null,
								},
							]
						: []
				}
				initialUniversityId={assignmentModalRow?.universityId || ''}
				initialUniversityName={assignmentModalRow?.university || ''}
				initialApplicationTargetId={
					assignmentModalRow?.applicationTargetId ||
					assignmentModalRow?.id ||
					''
				}
				tepeModal="applications"
				applicationModalInstanceKey={applicationModalInstanceKey}
				clearSelectionOnOpen={false}
				lockRecipients
				onSuccess={async () => {
					await refreshAppAssignments()
					await applicationDocumentsStore.reload()
				}}
			/>
		</div>
	)
})
